import { Colors, Icon, ProgressRing } from '@amelio/ui-library';
import { useState } from 'react';

import { CareerAspirationEditorModal } from './CareerAspirationEditorModal';
import { CareerStepsEditorModal } from './CareerStepsEditorModal';
import { GROWTH_GLASS_SURFACE, GROWTH_SECTION_PADDING_X } from './growthStyles';

import { EmptyState } from '@/components/EmptyState/EmptyState';
import { useAmelioTranslations } from '@/hooks';

/** The ring, in real pixels: 112px across with a 10px band, as the design draws it. */
const RING_SIZE_PX = 112;
const RING_STROKE_PX = 10;

/** The compass inside it, large enough to lead the card rather than decorate a corner of it. */
const RING_ICON_PX = 44;

/**
 * How much of the ring is drawn. Nothing is being measured here: the arc is deliberately left
 * unfinished, to say the journey has not started. It is therefore never announced.
 */
const RING_FILL_PERCENT = 72;

/**
 * The tab's own breathing room. The sides follow `GROWTH_SECTION_PADDING`, which every other
 * section uses; only the vertical rhythm is the invitation's own, since it stands alone on the tab.
 */
const INVITATION_PADDING = `${GROWTH_SECTION_PADDING_X} py-12`;

interface GrowthEmptyInvitationProps {
    employeeId: string;
    /** Empty while the directory has not answered, or when it never names the employee. */
    employeeFirstName: string;
    /** Hands over to the meetings page's planning wizard, on the "Aspirations de carrière" topic. */
    onPlanMeeting: () => void;
    dataTestId?: string;
}

/**
 * What the "Croissance" tab shows when it has nothing at all: one invitation instead of six empty
 * boxes each saying its own absence. Its ways out are every way of creating the first fact --
 * writing the first career step, recording the aspirations here and now, or planning the career
 * meeting that will gather them. It carries all of them because it replaces the sections that
 * would otherwise offer them: a way out left out here is a way out that exists nowhere.
 */
export const GrowthEmptyInvitation = ({
    employeeId,
    employeeFirstName,
    onPlanMeeting,
    dataTestId = 'growth-tab-empty',
}: GrowthEmptyInvitationProps) => {
    const { t } = useAmelioTranslations();
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [isStepsEditorOpen, setIsStepsEditorOpen] = useState(false);

    return (
        <section data-testid={dataTestId}>
            <EmptyState
                className={`${GROWTH_GLASS_SURFACE} ${INVITATION_PADDING}`}
                illustration={
                    <ProgressRing
                        value={RING_FILL_PERCENT}
                        size={RING_SIZE_PX}
                        strokeWidth={RING_STROKE_PX}
                        colorClassName={'stroke-primary'}
                        trackClassName={'stroke-growth-line'}
                        dataTestId={`${dataTestId}-ring`}
                    >
                        <Icon
                            name={'location-compass'}
                            size={RING_ICON_PX}
                            color={Colors.PRIMARY}
                            ariaHidden
                        />
                    </ProgressRing>
                }
                title={
                    employeeFirstName
                        ? t('performanceProfile.growth.empty.title', { name: employeeFirstName })
                        : t('performanceProfile.growth.empty.titleWithoutName')
                }
                message={t('performanceProfile.growth.empty.description')}
                tertiaryAction={{
                    label: t('performanceProfile.growth.timeline.addFirstStep'),
                    onClick: () => setIsStepsEditorOpen(true),
                }}
                secondaryAction={{
                    label: t('performanceProfile.growth.empty.recordAspirations'),
                    onClick: () => setIsEditorOpen(true),
                }}
                action={{
                    label: t('performanceProfile.growth.empty.planMeeting'),
                    onClick: onPlanMeeting,
                }}
            />

            <CareerAspirationEditorModal
                isOpen={isEditorOpen}
                onClose={() => setIsEditorOpen(false)}
                employeeId={employeeId}
                employeeFirstName={employeeFirstName}
                aspiration={null}
                thematicMeetingId={null}
                dataTestId={`${dataTestId}-editor`}
            />

            {/* An employee with nothing recorded has no step and no competency profile: that is
                what put this invitation on the tab in the first place. */}
            <CareerStepsEditorModal
                isOpen={isStepsEditorOpen}
                onClose={() => setIsStepsEditorOpen(false)}
                employeeId={employeeId}
                steps={[]}
                currentProfile={null}
                dataTestId={`${dataTestId}-steps-editor`}
            />
        </section>
    );
};
